import { __awaiter } from "tslib";
import * as React from 'react';
import { lazy, Suspense, useCallback, useState } from 'react';
// tslint:disable-next-line:import-blacklist
import { Route, Switch, withRouter } from 'react-router-dom';
import { Redirect, useRoutePath, AlternativeRoutingRedirector } from '@shared/routing';
import { useTranslation } from 'react-i18next';
import Helmet from 'react-helmet';
import PromoLoader from '../../PromoLoader/components/PromoLoader';
import Checkout from '../../Checkout';
import { DelayedPromoLoader } from '../../PromoLoader';
import { useConfig } from '../../context';
import Page404 from '../../Page404/Page404';
import { API_DATE_FORMAT, format, isRequireCurrencyParam } from '../../utils';
import { useTheme } from '../../theme';
import { useRenderers } from '../../renderProps';
import { useRunSearchMutation, useSearchResultsQuery } from '@websky/graphql';
import { useSchedule } from '../../Schedule/hooks';
import { CURRENCY_KEY, set } from '../../cache';
import getOrderDataLayer, { createDataLayer } from '../../DataLayerAnalytics/orderDataLayerInstance';
import { OrderType } from '../../DataLayerAnalytics/OrderDataLayer/types';
import { initTagManager } from '../../DataLayerAnalytics/tagManager';
import { extractPromo, hasPromo } from './FastSearch/utils';
import { ApolloError } from 'apollo-boost';
import { EmptyPricesForFareGroupsError, EmptySearchResultSegmentsError } from './errors';
import { useCurrentUser } from '../../CurrentUser/CurrentUser.hooks';
import ConfirmOptions from './ConfirmOptions/ConfirmOptions';
import { getUrlWithServicesParams } from '../../Checkout/utils';
import { Log } from '../../sentry';
const SearchForm = lazy(() => import('../../SearchForm'));
const RestoreOrder = lazy(() => import('./RestoreOrder/RestoreOrder'));
const FindOrder = lazy(() => import('../../Checkout/components/Checkout/FindOrder/FindOrder'));
const MetaRedirect = lazy(() => import('./MetaRedirect/MetaRedirect'));
const ResultsError = lazy(() => import('../../ResultsError'));
const FastSearch = lazy(() => import('./FastSearch/FastSearch'));
const CreateOrder = lazy(() => import('./CreateOrder/CreateOrder'));
const ResultsWrapper = lazy(() => import('../../Results/components/ResultsWrapper/ResultsWrapper'));
const Schedule = lazy(() => import('../../Schedule'));
createDataLayer(OrderType.SearchResults);
const Inner = ({ history, location }) => {
    useCurrentUser();
    const { t } = useTranslation();
    const { Inner: css } = useTheme('Inner');
    const [isRedirectFromMeta, setIsRedirectFromMeta] = useState(false);
    const [order, setOrder] = useState(null);
    const [results, setResults] = useState();
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isConfirmOptionLoading, setIsConfirmOptionLoading] = useState(false);
    const [orderLoading, setOrderLoading] = useState(false);
    const { Engine: { searchFormURL }, global: { companyInfo: { iataCode }, allowPromoCodes } } = useConfig();
    const [searchMutation] = useRunSearchMutation();
    const resultsQuery = useSearchResultsQuery({ fetchPolicy: 'no-cache', skip: true });
    const [promoCode, setPromoCode] = useState(undefined);
    const routePath = useRoutePath();
    const { SearchForm: SearchFormOverride = SearchForm, FastSearch: FastSearchOverride = FastSearch } = useRenderers();
    React.useEffect(() => {
        initTagManager();
    }, []);
    const handleOrderLoaded = useCallback((order) => {
        setOrder(order);
    }, [order, setOrder]);
    const orderLoadingHandler = useCallback(orderLoading => {
        setOrderLoading(orderLoading);
    }, []);
    const handleRenderConfirmOptions = () => {
        if (order && !orderLoading) {
            getOrderDataLayer(OrderType.Checkout).setOrder(order);
            return (React.createElement(ConfirmOptions, { order: order, handleOrderLoaded: handleOrderLoaded, onLoadingChange: setIsConfirmOptionLoading }));
        }
        return null;
    };
    const [getSchedule, { schedule, defaultParameters, availableDates, isLoading: scheduleLoading }] = useSchedule();
    const dismissMetaWarning = useCallback(() => {
        setIsRedirectFromMeta(false);
    }, []);
    const onError = useCallback((e) => {
        var _a, _b;
        let error = new Error();
        error.name = t('It looks like something went wrong');
        error.message = t('Please reload the page or start a new search');
        if (e instanceof ApolloError && ((_b = (_a = e.graphQLErrors) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.originalError)) {
            error = e.graphQLErrors[0].originalError;
        }
        else if (e instanceof Error) {
            error = e;
        }
        setError(error);
        console.error(e);
    }, [setError]);
    const loadResults = useCallback((id) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f;
        setOrder(null);
        setIsLoading(true);
        try {
            const { data } = yield resultsQuery.refetch({ id });
            if (!((_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.SearchResult) === null || _a === void 0 ? void 0 : _a.searchParameters) === null || _b === void 0 ? void 0 : _b.segments) === null || _c === void 0 ? void 0 : _c.length)) {
                throw new EmptySearchResultSegmentsError();
            }
            if ((_e = (_d = data === null || data === void 0 ? void 0 : data.SearchResult) === null || _d === void 0 ? void 0 : _d.flightDirections) === null || _e === void 0 ? void 0 : _e.some(direction => direction.legs.every(leg => !leg.pricesForFareGroups))) {
                Log({
                    message: EmptyPricesForFareGroupsError.name,
                    data: data.SearchResult,
                    level: 'error'
                });
                throw new EmptyPricesForFareGroupsError();
            }
            setResults((_f = data === null || data === void 0 ? void 0 : data.SearchResult) !== null && _f !== void 0 ? _f : null);
        }
        catch (error) {
            onError(error);
        }
        setIsLoading(false);
    }), [resultsQuery.refetch, setOrder, setIsLoading, onError]);
    const startSearch = useCallback((params, replace = true) => __awaiter(void 0, void 0, void 0, function* () {
        setIsLoading(true);
        setError(null);
        if (params.currency) {
            set(CURRENCY_KEY, params.currency);
        }
        if (replace) {
            history.replace('/results');
        }
        else {
            history.push('/results');
        }
        try {
            const data = yield searchMutation({
                variables: {
                    params: Object.assign(Object.assign({}, params), { currency: isRequireCurrencyParam() ? params.currency : undefined })
                }
            });
            if (data.errors) {
                throw new Error(data.errors[0].message);
            }
            else if (data.data.RunGeneralSearch && data.data.RunGeneralSearch.id) {
                if (replace) {
                    history.push('/results/' + data.data.RunGeneralSearch.id);
                }
                else {
                    history.replace('/results/' + data.data.RunGeneralSearch.id);
                }
                loadResults(data.data.RunGeneralSearch.id);
            }
            else {
                throw new Error('No results ID');
            }
        }
        catch (error) {
            setError({
                name: t('It looks like something went wrong'),
                message: `${t('Please reload the page or start a new search')}`
            });
            console.error(error);
            setIsLoading(false);
        }
    }), []);
    const onOrderRecieved = useCallback((order, replace = false) => {
        if (order) {
            setOrder(order);
        }
        history.push(getUrlWithServicesParams(`/booking/${order.id}`, location));
    }, [history, location]);
    const redirectToPayment = (order) => {
        if (order) {
            setOrder(order);
            history.push(`/booking/${order.id}/payment`);
        }
    };
    const onMetasearchResultsRedirect = (resultsId) => {
        setIsRedirectFromMeta(true);
        history.replace('/results/' + resultsId);
    };
    const onScheduleSearch = useCallback(({ parameters, filters, date, reload }) => {
        const pathname = history.location.pathname;
        const directOnlyParam = (filters === null || filters === void 0 ? void 0 : filters.directOnly) ? '/directOnly' : '';
        if (date) {
            getSchedule(parameters, date);
            const newPath = `${pathname.replace(/\/directOnly\/?$/, '')}${directOnlyParam}`;
            if (pathname !== newPath) {
                history.push(newPath);
            }
        }
        else {
            const path = `/schedule/${parameters.departure}/${parameters.arrival}${directOnlyParam}`;
            if (pathname !== path) {
                history.push(path);
                if (reload) {
                    window.location.reload();
                }
            }
        }
    }, [history, getSchedule]);
    const isScheduleAvailable = ['D2', 'ZZ', 'YC', 'DV', 'KV', 'Y7', '6R', 'R3'].includes(iataCode);
    return (React.createElement("div", { style: { flexBasis: '100%' }, className: css.inner },
        React.createElement(Helmet, null,
            React.createElement("title", null,
                t('Title:companyName'),
                " \u2013 ",
                t('Title:Find tickets'))),
        React.createElement(Suspense, { fallback: React.createElement(PromoLoader, null) },
            React.createElement(AlternativeRoutingRedirector, null),
            React.createElement(DelayedPromoLoader, { loading: isLoading || scheduleLoading },
                React.createElement(Switch, null,
                    React.createElement(Route, { path: routePath('/'), exact: true, render: () => {
                            if (searchFormURL) {
                                window.location.href = searchFormURL;
                                return null;
                            }
                            return React.createElement(SearchFormOverride, { onSubmit: startSearch, promoCode: promoCode });
                        } }),
                    React.createElement(Route, { path: routePath('/search/:departure/:arrival'), exact: true, render: routeProps => (React.createElement(SearchFormOverride, { key: routeProps.location.pathname, onSubmit: startSearch, defaultParameters: {
                                currency: null,
                                segments: [
                                    {
                                        arrival: {
                                            iata: routeProps.match.params['arrival'],
                                            city: null,
                                            name: null
                                        },
                                        departure: {
                                            iata: routeProps.match.params['departure'],
                                            city: null,
                                            name: null
                                        },
                                        date: format(new Date(), API_DATE_FORMAT)
                                    }
                                ],
                                passengers: null,
                                promotionCode: null,
                                ffpMode: null
                            } })) }),
                    React.createElement(Route, { path: routePath('/restore/:orderId/:token'), exact: true, render: () => (React.createElement(RestoreOrder, { onOrderReceived: onOrderRecieved })) }),
                    React.createElement(Route, { path: routePath(['/find/:id?/:secret?', '/find/:id?/:secret?/submit']), exact: true, render: routeProps => {
                            const callSubmit = routeProps.match.path.includes('submit');
                            return React.createElement(FindOrder, { onSuccess: onOrderRecieved, callSubmit: callSubmit });
                        } }),
                    React.createElement(Route, { path: routePath(['/payment/:id?/:secret?', '/payment/:id?/:secret?/submit']), exact: true, render: routeProps => {
                            const callSubmit = routeProps.match.path.includes('submit');
                            return React.createElement(FindOrder, { onSuccess: redirectToPayment, callSubmit: callSubmit });
                        } }),
                    React.createElement(Route, { path: routePath('/metasearch/:token'), exact: true, render: () => (React.createElement(MetaRedirect, { onResultsRedirect: onMetasearchResultsRedirect, onSuccess: onOrderRecieved })) }),
                    React.createElement(Route, { path: routePath('/results'), exact: true, render: () => {
                            if (error) {
                                return (React.createElement(React.Fragment, null,
                                    React.createElement(SearchFormOverride, { onSubmit: startSearch }),
                                    React.createElement(ResultsError, { header: error.name, body: error.message })));
                            }
                            return !error && !isLoading ? React.createElement(Redirect, { to: "/" }) : null;
                        } }),
                    React.createElement(Route, { path: routePath('/search/:fastSearch'), exact: true, render: props => (React.createElement(FastSearchOverride, { startSearch: startSearch, fastSearch: props.match.params.fastSearch, redirect: React.createElement(Redirect, { to: "/" }) })) }),
                    React.createElement(Route, { path: routePath('/results/create-order/:flightIds+'), render: props => (React.createElement(CreateOrder, { flightsIds: props.match.params.flightIds, redirectOnFail: React.createElement(Redirect, { to: '/' }), onSuccess: onOrderRecieved })) }),
                    React.createElement(Route, { path: routePath('/results/:id(\\d+/?)+'), render: props => {
                            return (React.createElement(ResultsWrapper, { order: order, isOrderLoading: orderLoading, isConfirmOptionLoading: isConfirmOptionLoading, renderConfirmOptions: handleRenderConfirmOptions, showMetaWarning: isRedirectFromMeta, onMetaWarningDismiss: dismissMetaWarning, onOrderLoaded: handleOrderLoaded, onOrderLoading: orderLoadingHandler, searchResults: results, errors: error ? [error] : null, startNewSearch: startSearch, onProceed: onOrderRecieved, resultId: props.match.params.id, loadResults: loadResults, isLoading: isLoading, error: error }));
                        } }),
                    React.createElement(Route, { path: routePath('/booking/:id(\\d+/?)+'), render: props => (React.createElement(React.Fragment, null,
                            React.createElement(Helmet, null,
                                React.createElement("title", null,
                                    t('Title:companyName'),
                                    " \u2013 ",
                                    t('Title:Checkout order'))),
                            React.createElement(Checkout, { startNewSearch: startSearch, orderId: props.match.params.id, order: order }))) }),
                    isScheduleAvailable && (React.createElement(Route, { path: routePath('/schedule'), exact: true, render: () => {
                            if (scheduleLoading) {
                                return null;
                            }
                            return React.createElement(Schedule, { onScheduleSearch: onScheduleSearch });
                        } })),
                    isScheduleAvailable && (React.createElement(Route, { path: routePath('/schedule/:departure([A-Z]{3,3})/:arrival([A-Z]{3,3})/:filters(directOnly)?'), render: props => {
                            var _a;
                            const params = props.match.params;
                            const segment = (_a = defaultParameters === null || defaultParameters === void 0 ? void 0 : defaultParameters.segments) === null || _a === void 0 ? void 0 : _a[0];
                            const filters = {
                                directOnly: !!params.filters
                            };
                            const isOtherCities = segment &&
                                (segment.departure.iata !== params.departure ||
                                    segment.arrival.iata !== params.arrival);
                            if ((!isLoading && !scheduleLoading && !schedule) || isOtherCities) {
                                getSchedule({ departure: params.departure, arrival: params.arrival });
                                return null;
                            }
                            if (scheduleLoading) {
                                return null;
                            }
                            return (React.createElement(Schedule, { defaultParameters: defaultParameters, filters: filters, schedule: schedule, availableDates: availableDates, onStartSearch: startSearch, onScheduleSearch: onScheduleSearch }));
                        } })),
                    React.createElement(Route, { path: routePath('/:fastSearch'), exact: true, render: props => {
                            const searchParams = props.match.params.fastSearch;
                            if (allowPromoCodes && hasPromo(searchParams)) {
                                const promoCode = extractPromo(searchParams);
                                if (promoCode) {
                                    setPromoCode(extractPromo(searchParams));
                                }
                            }
                            return React.createElement(Redirect, { to: "/" });
                        } }),
                    React.createElement(Page404, { onClick: () => (window.location.href = '/') }))))));
};
export default withRouter(Inner);
